import React, { useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from "react-native";
import GlobalStyles from "./StylenColor";
import { Ionicons } from "@expo/vector-icons";

// Desktop Theme colors (inline to avoid circular dependency)
const DesktopColors = {
  primary: '#059669',
  primaryHover: '#047857',
  cardBg: '#ffffff',
  contentBg: '#f8fafc',
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
};

// Desktop Modal Component
const DesktopTermsModal = ({ visible, onClose }) => {
  const [hoveredClose, setHoveredClose] = useState(false);

  if (!visible) return null;

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={desktopStyles.overlay}>
        <View style={desktopStyles.modalContainer}>
          {/* Header */}
          <View style={desktopStyles.header}>
            <View style={desktopStyles.headerContent}>
              <Ionicons name="document-text-outline" size={24} color={DesktopColors.primary} />
              <Text style={desktopStyles.title}>Terms & Conditions and Privacy Policy</Text>
            </View>
            <TouchableOpacity
              style={[desktopStyles.closeIconBtn, hoveredClose && desktopStyles.closeIconBtnHover]}
              onPress={onClose}
              onMouseEnter={() => setHoveredClose(true)}
              onMouseLeave={() => setHoveredClose(false)}
            >
              <Ionicons name="close" size={24} color={DesktopColors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView
            style={desktopStyles.scrollContainer}
            showsVerticalScrollIndicator={true}
          >
            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionTitle}>Sowin ESG App – Privacy Policy</Text>
              <Text style={desktopStyles.meta}>Effective Date: 16 June 2025</Text>
              <Text style={desktopStyles.meta}>Company: Sowin Enterprises</Text>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>1. Introduction</Text>
              <Text style={desktopStyles.content}>
                Sowin Enterprises ("Sowin", "we", "our", or "us") respects your privacy. This Privacy Policy explains how we collect, use, share, and protect your information when you use the Sowin ESG App ("App"). The App is designed to support ESG (Environmental, Social, and Governance) data collection, advisory, and analytics.
              </Text>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>2. Data We Collect</Text>
              <View style={desktopStyles.bulletList}>
                <Text style={desktopStyles.bulletItem}>• User Information: Name, email, contact number, designation, Payroll, Location, Photograph, etc.</Text>
                <Text style={desktopStyles.bulletItem}>• ESG Data: Inputs related to energy, emissions, safety, diversity, etc.</Text>
                <Text style={desktopStyles.bulletItem}>• Stakeholder Feedback: Name, Invoices, Survey responses, concerns, or suggestions.</Text>
                <Text style={desktopStyles.bulletItem}>• Usage Data: Device information, IP address, interaction logs.</Text>
              </View>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>3. Purpose of Data Collection</Text>
              <Text style={desktopStyles.content}>Your data is collected and processed to:</Text>
              <View style={desktopStyles.bulletList}>
                <Text style={desktopStyles.bulletItem}>• Enable ESG tracking, benchmarking, and reporting</Text>
                <Text style={desktopStyles.bulletItem}>• Provide ESG guidance and performance analytics</Text>
                <Text style={desktopStyles.bulletItem}>• Generate anonymized industry trends and insights</Text>
                <Text style={desktopStyles.bulletItem}>• Ensure secure user experience and improve our services</Text>
                <Text style={desktopStyles.bulletItem}>• Comply with applicable legal and regulatory obligations</Text>
              </View>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>4. Data Sharing and Disclosure</Text>
              <Text style={desktopStyles.content}>
                We do not sell your personal or organizational data. We may share anonymized data for reporting purposes. Personal and confidential data may only be shared:
              </Text>
              <View style={desktopStyles.bulletList}>
                <Text style={desktopStyles.bulletItem}>• With regulatory or ESG authorities upon lawful request</Text>
                <Text style={desktopStyles.bulletItem}>• With authorized users in your organization</Text>
                <Text style={desktopStyles.bulletItem}>• With trusted service providers (e.g., hosting, payment) under strict confidentiality</Text>
              </View>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>5. Data Security Measures</Text>
              <Text style={desktopStyles.content}>
                We implement strong security protocols aligned with ISO/IEC 27001 and ISO/IEC 27701 standards. Our database is powered by Google Firestore and includes the following safeguards:
              </Text>
              <View style={desktopStyles.bulletList}>
                <Text style={desktopStyles.bulletItem}>• TLS 1.3 encryption for data in transit</Text>
                <Text style={desktopStyles.bulletItem}>• AES-256 encryption for data at rest</Text>
                <Text style={desktopStyles.bulletItem}>• Firestore security rules for role-based access control</Text>
                <Text style={desktopStyles.bulletItem}>• Regular security audits and vulnerability testing</Text>
                <Text style={desktopStyles.bulletItem}>• Authentication-based identity verification (e.g., Firebase Auth)</Text>
                <Text style={desktopStyles.bulletItem}>• Regular monitoring and compliance audits by Google Cloud</Text>
              </View>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>6. Confidentiality Assurance</Text>
              <Text style={desktopStyles.content}>
                All organization-specific data is treated as confidential. Access is restricted to authorized personnel. Confidentiality agreements are signed by our staff and third-party partners.
              </Text>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>7. Data Retention</Text>
              <Text style={desktopStyles.content}>
                We retain your data as long as necessary for the purposes outlined. After account deactivation or end of subscription, data is retained for 90 days, after which it is securely deleted unless legal retention is required.
              </Text>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>8. Your Rights</Text>
              <Text style={desktopStyles.content}>You have the right to:</Text>
              <View style={desktopStyles.bulletList}>
                <Text style={desktopStyles.bulletItem}>• Access and export your data</Text>
                <Text style={desktopStyles.bulletItem}>• Correct or update inaccurate information</Text>
                <Text style={desktopStyles.bulletItem}>• Request deletion of your personal data</Text>
                <Text style={desktopStyles.bulletItem}>• Withdraw consent at any time</Text>
              </View>
              <Text style={desktopStyles.content}>
                To exercise these rights, contact us at: sowingroups@gmail.com
              </Text>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>9. Children's Privacy</Text>
              <Text style={desktopStyles.content}>
                Our services are intended for enterprise use. We do not knowingly collect data from individuals under the age of 18.
              </Text>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>10. Changes to This Policy</Text>
              <Text style={desktopStyles.content}>
                We may update this Privacy Policy periodically. We will notify users via the App or email when significant changes are made. Continued use of the App indicates acceptance of the updated policy.
              </Text>
            </View>

            <View style={desktopStyles.section}>
              <Text style={desktopStyles.sectionHeading}>11. Contact Information</Text>
              <Text style={desktopStyles.content}>
                For any questions or concerns about this Privacy Policy, please contact us at:
              </Text>
              <View style={desktopStyles.contactCard}>
                <Text style={desktopStyles.contactLine}>Grievance Officer: Mr. Dinesh Kadam</Text>
                <Text style={desktopStyles.contactLine}>Email: dineshmkadam@gmail.com</Text>
                <Text style={desktopStyles.contactLine}>Sowin Enterprises, Chiplun, Maharashtra, India</Text>
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={desktopStyles.footer}>
            <TouchableOpacity
              style={[desktopStyles.closeBtn, hoveredClose && desktopStyles.closeBtnHover]}
              onPress={onClose}
            >
              <Text style={desktopStyles.closeBtnText}>I Understand</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: DesktopColors.cardBg,
    borderRadius: 16,
    width: 700,
    maxHeight: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 20,
    elevation: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: DesktopColors.border,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontFamily: 'LatoBlack',
    color: DesktopColors.textPrimary,
  },
  closeIconBtn: {
    padding: 8,
    borderRadius: 8,
  },
  closeIconBtnHover: {
    backgroundColor: DesktopColors.contentBg,
  },
  scrollContainer: {
    maxHeight: 500,
    paddingHorizontal: 24,
  },
  section: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: DesktopColors.border,
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: 'LatoBlack',
    color: DesktopColors.primary,
    marginBottom: 8,
  },
  meta: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textMuted,
    marginBottom: 2,
  },
  sectionHeading: {
    fontSize: 16,
    fontFamily: 'LatoBlack',
    color: DesktopColors.textPrimary,
    marginBottom: 8,
  },
  content: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textSecondary,
    lineHeight: 22,
  },
  bulletList: {
    marginTop: 8,
    gap: 6,
  },
  bulletItem: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textSecondary,
    lineHeight: 22,
    paddingLeft: 8,
  },
  contactCard: {
    backgroundColor: DesktopColors.contentBg,
    padding: 16,
    borderRadius: 8,
    marginTop: 12,
  },
  contactLine: {
    fontSize: 14,
    fontFamily: 'LatoRegular',
    color: DesktopColors.textPrimary,
    marginBottom: 4,
  },
  footer: {
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: DesktopColors.border,
  },
  closeBtn: {
    backgroundColor: DesktopColors.primary,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  closeBtnHover: {
    backgroundColor: DesktopColors.primaryHover,
  },
  closeBtnText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'LatoBlack',
    fontWeight: '600',
  },
});

const TermsModal = ({ visible, onClose }) => {
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && width > 1024;

  // Use desktop modal on desktop web
  if (isDesktopWeb) {
    return <DesktopTermsModal visible={visible} onClose={onClose} />;
  }

  // Mobile Modal
  return (
    <Modal
      animationType="slide"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalView}>
          <ScrollView>
            <Text style={styles.title}>
              Terms & Conditions and Privacy Policy
            </Text>

            <Text style={styles.content}>
              Sowin ESG App – Privacy Policy{"\n"}
              Effective Date: 16 June 2025{"\n"}
              Company: Sowin Enterprises{"\n\n"}
              1. Introduction{"\n"}
              Sowin Enterprises (“Sowin”, “we”, “our”, or “us”) respects your
              privacy. This Privacy Policy explains how we collect, use, share,
              and protect your information when you use the Sowin ESG App
              (“App”). The App is designed to support ESG (Environmental,
              Social, and Governance) data collection, advisory, and analytics.
              {"\n\n"}
              2. Data We Collect{"\n"}- User Information: Name, email, contact
              number, designation, Payroll, Location, Photograph, etc.{"\n"}-
              ESG Data: Inputs related to energy, emissions, safety, diversity,
              etc.{"\n"}- Stakeholder Feedback: Name, Invoices, Survey
              responses, concerns, or suggestions.{"\n"}- Usage Data: Device
              information, IP address, interaction logs.
              3. Purpose of Data Collection{"\n"}
              Your data is collected and processed to:{"\n"}- Enable ESG
              tracking, benchmarking, and reporting{"\n"}- Provide ESG guidance
              and performance analytics{"\n"}- Generate anonymized industry
              trends and insights{"\n"}- Ensure secure user experience and
              improve our services{"\n"}- Comply with applicable legal and
              regulatory obligations{"\n\n"}
              4. Data Sharing and Disclosure{"\n"}
              We do not sell your personal or organizational data. We may share
              anonymized data for reporting purposes. Personal and confidential
              data may only be shared:{"\n"}- With regulatory or ESG authorities
              upon lawful request{"\n"}- With authorized users in your
              organization{"\n"}- With trusted service providers (e.g., hosting,
              payment) under strict confidentiality{"\n\n"}
              5. Data Security Measures{"\n"}
              We implement strong security protocols aligned with ISO/IEC 27001
              and ISO/IEC 27701 standards. Our database is powered by Google
              Firestore and includes the following safeguards:
              {"\n"}- TLS 1.3 encryption for data in transit{"\n"}- AES-256
              encryption for data at rest{"\n"}- Firestore security rules for
              role-based access control{"\n"}- Regular security audits and
              vulnerability testing{"\n"}- Authentication-based identity
              verification (e.g., Firebase Auth){"\n"}- Regular monitoring and
              compliance audits by Google Cloud{"\n\n"}
              6. Confidentiality Assurance{"\n"}
              All organization-specific data is treated as confidential. Access
              is restricted to authorized personnel. Confidentiality agreements
              are signed by our staff and third-party partners.{"\n\n"}
              7. Data Retention{"\n"}
              We retain your data as long as necessary for the purposes
              outlined. After account deactivation or end of subscription, data
              is retained for 90 days, after which it is securely deleted unless
              legal retention is required.{"\n\n"}
              8. Your Rights{"\n"}
              You have the right to:{"\n"}- Access and export your data{"\n"}-
              Correct or update inaccurate information{"\n"}- Request deletion
              of your personal data{"\n"}- Withdraw consent at any time{"\n"}
              To exercise these rights, contact us at:
              sowingroups@gmail.com{"\n\n"}
              9. Children’s Privacy{"\n"}
              Our services are intended for enterprise use. We do not knowingly
              collect data from individuals under the age of 18.{"\n\n"}
              10. Changes to This Policy{"\n"}
              We may update this Privacy Policy periodically. We will notify
              users via the App or email when significant changes are made.
              Continued use of the App indicates acceptance of the updated
              policy.{"\n\n"}
              11. Contact Information{"\n"}
              For any questions or concerns about this Privacy Policy, please
              contact us at:{"\n"}
              Grievance Officer: Mr. Dinesh Kadam{"\n"}
              Email: dineshmkadam@gmail.com{"\n"}
              Sowin Enterprises, Chiplun, Maharashtra, India
            </Text>

            <TouchableOpacity onPress={onClose} style={[styles.closeBtn, GlobalStyles.btnBgShadow]}>
              <Text style={[styles.closeText, GlobalStyles.btntxtoffset]}>Close</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalView: {
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: 20,
    width: "90%",
    maxHeight: "80%",
  },
  title: {
    fontSize: GlobalStyles.fontSize.sm,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 12,
  },
  content: {
    fontSize: GlobalStyles.fontSize.xsss,
    lineHeight: 20,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    marginBottom: 30,
  },
  closeBtn: {
    width: "100%",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
    alignSelf:"center",
    maxWidth: 500,
    marginBottom: 10,
  },
  closeText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.xs,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
});

export default TermsModal;
